import React, { useState, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useAuditLog } from '../hooks/useAuditLog'
import { supabase } from '../lib/supabase'
import {
  Upload,
  File,
  Image,
  FileText,
  X,
  Check,
  AlertCircle,
  Download,
  Eye,
  Trash2,
  Shield
} from 'lucide-react'
import { toast } from 'sonner'

interface MedicalDocument {
  id: string
  name: string
  type: string
  size: number
  url: string
  uploaded_at: string
  record_id?: string
  is_encrypted: boolean
}

interface MedicalDocumentUploadProps {
  recordId?: string
  patientId: string
  onDocumentUploaded?: (document: MedicalDocument) => void
  maxFiles?: number
  allowedTypes?: string[]
  maxSizePerFile?: number // em MB
}

const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain'
]

const MAX_SIZE_MB = 10

export default function MedicalDocumentUpload({
  recordId,
  patientId,
  onDocumentUploaded,
  maxFiles = 5,
  allowedTypes = ALLOWED_TYPES,
  maxSizePerFile = MAX_SIZE_MB
}: MedicalDocumentUploadProps) {
  const { user } = useAuth()
  const { logAudit } = useAuditLog()
  const [uploading, setUploading] = useState(false)
  const [documents, setDocuments] = useState<MedicalDocument[]>([])
  const [dragActive, setDragActive] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({})

  // Carregar documentos existentes
  React.useEffect(() => {
    if (recordId) {
      loadDocuments()
    }
  }, [recordId])

  const loadDocuments = async () => {
    if (!recordId) return

    try {
      const { data, error } = await supabase
        .from('medical_documents')
        .select('*')
        .eq('record_id', recordId)
        .order('uploaded_at', { ascending: false })

      if (error) {
        console.error('Erro ao carregar documentos:', error)
        return
      }

      setDocuments(data || [])
    } catch (error) {
      console.error('Erro ao carregar documentos:', error)
    }
  }

  const validateFile = (file: File): string | null => {
    if (!allowedTypes.includes(file.type)) {
      return `Tipo de arquivo não permitido: ${file.type}`
    }

    if (file.size > maxSizePerFile * 1024 * 1024) {
      return `Arquivo muito grande. Máximo: ${maxSizePerFile}MB`
    }

    return null
  }

  const uploadFile = async (file: File): Promise<MedicalDocument | null> => {
    if (!user) return null

    const validation = validateFile(file)
    if (validation) {
      toast.error(validation)
      return null
    }

    try {
      // Gerar nome único para o arquivo
      const fileExt = file.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`
      const filePath = `medical-documents/${patientId}/${fileName}`

      // Simular progresso de upload
      const fileId = Math.random().toString(36)
      setUploadProgress(prev => ({ ...prev, [fileId]: 0 }))

      // Simular upload progressivo
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const current = prev[fileId] || 0
          if (current >= 90) {
            clearInterval(progressInterval)
            return prev
          }
          return { ...prev, [fileId]: current + 10 }
        })
      }, 200)

      // Upload para Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('medical-files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        })

      clearInterval(progressInterval)
      setUploadProgress(prev => ({ ...prev, [fileId]: 100 }))

      if (uploadError) {
        throw uploadError
      }

      // Obter URL pública
      const { data: urlData } = supabase.storage
        .from('medical-files')
        .getPublicUrl(filePath)

      // Salvar metadados no banco
      const documentData = {
        name: file.name,
        type: file.type,
        size: file.size,
        url: urlData.publicUrl,
        record_id: recordId,
        patient_id: patientId,
        uploaded_by: user.id,
        is_encrypted: true,
        storage_path: filePath
      }

      const { data: dbData, error: dbError } = await supabase
        .from('medical_documents')
        .insert(documentData)
        .select()
        .single()

      if (dbError) {
        // Limpar arquivo do storage se falhar no banco
        await supabase.storage.from('medical-files').remove([filePath])
        throw dbError
      }

      const newDocument: MedicalDocument = {
        id: dbData.id,
        name: file.name,
        type: file.type,
        size: file.size,
        url: urlData.publicUrl,
        uploaded_at: dbData.created_at,
        record_id: recordId,
        is_encrypted: true
      }

      // Log de auditoria para upload de documento
      await logAudit({
        action_type: 'create',
        resource_type: 'document',
        resource_id: dbData.id,
        resource_description: `Upload de documento: ${file.name}`,
        metadata: {
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
          record_id: recordId,
          patient_id: patientId,
          is_encrypted: true,
          storage_path: filePath
        }
      })

      // Limpar progresso
      setTimeout(() => {
        setUploadProgress(prev => {
          const newProgress = { ...prev }
          delete newProgress[fileId]
          return newProgress
        })
      }, 1000)

      return newDocument
    } catch (error) {
      console.error('Erro no upload:', error)
      toast.error('Erro ao fazer upload do arquivo')
      return null
    }
  }

  const handleFileSelect = async (files: FileList) => {
    if (!user) return

    const fileArray = Array.from(files)
    
    if (fileArray.length > maxFiles) {
      toast.error(`Máximo de ${maxFiles} arquivos permitidos`)
      return
    }

    if (documents.length + fileArray.length > maxFiles) {
      toast.error(`Máximo de ${maxFiles} documentos por registro`)
      return
    }

    setUploading(true)

    try {
      const uploadPromises = fileArray.map(uploadFile)
      const results = await Promise.all(uploadPromises)
      
      const successfulUploads = results.filter(Boolean) as MedicalDocument[]
      
      if (successfulUploads.length > 0) {
        setDocuments(prev => [...successfulUploads, ...prev])
        successfulUploads.forEach(doc => {
          onDocumentUploaded?.(doc)
        })
        toast.success(`${successfulUploads.length} arquivo(s) enviado(s) com sucesso`)
      }
    } catch (error) {
      console.error('Erro no upload:', error)
      toast.error('Erro ao fazer upload dos arquivos')
    } finally {
      setUploading(false)
    }
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
    
    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFileSelect(files)
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
  }, [])

  const deleteDocument = async (documentId: string) => {
    try {
      // Buscar dados do documento antes de excluir
      const documentToDelete = documents.find(doc => doc.id === documentId)
      
      const { error } = await supabase
        .from('medical_documents')
        .delete()
        .eq('id', documentId)

      if (error) throw error

      // Log de auditoria para exclusão de documento
      await logAudit({
        action_type: 'delete',
        resource_type: 'document',
        resource_id: documentId,
        resource_description: `Exclusão de documento: ${documentToDelete?.name || 'Documento médico'}`,
        metadata: {
          file_name: documentToDelete?.name,
          file_type: documentToDelete?.type,
          file_size: documentToDelete?.size,
          record_id: recordId,
          patient_id: patientId,
          deleted_by: user?.id
        }
      })

      setDocuments(prev => prev.filter(doc => doc.id !== documentId))
      toast.success('Documento removido com sucesso')
    } catch (error) {
      console.error('Erro ao remover documento:', error)
      toast.error('Erro ao remover documento')
    }
  }

  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return <Image className="h-5 w-5" />
    if (type === 'application/pdf') return <FileText className="h-5 w-5" />
    return <File className="h-5 w-5" />
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="space-y-4">
      {/* Área de Upload */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Enviar Documentos Médicos
        </h3>
        <p className="text-gray-600 mb-4">
          Arraste arquivos aqui ou clique para selecionar
        </p>
        <input
          type="file"
          multiple
          accept={allowedTypes.join(',')}
          onChange={(e) => e.target.files && handleFileSelect(e.target.files)}
          className="hidden"
          id="file-upload"
          disabled={uploading}
        />
        <label
          htmlFor="file-upload"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer disabled:opacity-50"
        >
          <Upload className="h-4 w-4 mr-2" />
          Selecionar Arquivos
        </label>
        <p className="text-xs text-gray-500 mt-2">
          Máximo {maxFiles} arquivos, {maxSizePerFile}MB cada
        </p>
      </div>

      {/* Progresso de Upload */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="space-y-2">
          {Object.entries(uploadProgress).map(([fileId, progress]) => (
            <div key={fileId} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Enviando...</span>
                <span className="text-sm font-medium">{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lista de Documentos */}
      {documents.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-200">
            <h4 className="text-lg font-medium text-gray-900">
              Documentos ({documents.length})
            </h4>
          </div>
          <div className="divide-y divide-gray-200">
            {documents.map((doc) => (
              <div key={doc.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="text-gray-400">
                      {getFileIcon(doc.type)}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {doc.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatFileSize(doc.size)} • {new Date(doc.uploaded_at).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                    {doc.is_encrypted && (
                      <Shield className="h-4 w-4 text-green-600" />
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => window.open(doc.url, '_blank')}
                      className="text-blue-600 hover:text-blue-800 transition-colors"
                      title="Visualizar"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        const link = document.createElement('a')
                        link.href = doc.url
                        link.download = doc.name
                        link.click()
                      }}
                      className="text-green-600 hover:text-green-800 transition-colors"
                      title="Baixar"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => deleteDocument(doc.id)}
                      className="text-red-600 hover:text-red-800 transition-colors"
                      title="Remover"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}